import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useGoogleTranslate } from '../../../Hooks/useGoogleTranslate';
import { Col, Container, Form, Nav, Row, Spinner, Tab, Pagination } from 'react-bootstrap';
import { usePageTitle } from '../../../Hooks/usePageTitle';
import {
  getVirtualGifts,
  getMyVirtualGifts,
  getGreetingCards,
  getMyGreetingCards,
  purchaseGift
} from '../../../Services/User/GiftStore';
import { showToast } from '../../../Components/Toast/toast';
import smallCoinIcon from '../../../assets/images/small-coin.png';
import heartDecor from '../../../assets/images/coin-store-figma/decor-heart.svg';
import CustomButton from '../../../Components/CustomButton';
import { FaGift } from 'react-icons/fa';
import { FiSearch } from 'react-icons/fi';
import './GiftStore.css';
import useUserStore from '../../../Stores/UserStore';
import { translateText } from '../../../Services/GoogleTranslate';

const GiftStore = () => {
  const pageTitle = useGoogleTranslate('Gift Store');
  const greetingCardsPageTitle = useGoogleTranslate('Greeting Cards');
  const giftsLabel = useGoogleTranslate('Gifts');
  const greetingCardsLabel = useGoogleTranslate('Greeting Cards');
  const buyGiftsLabel = useGoogleTranslate('Buy Gifts');
  const buyGreetingCardsLabel = useGoogleTranslate('Buy Greeting Cards');
  const myGiftsLabel = useGoogleTranslate('My Gifts');
  const myGreetingCardsLabel = useGoogleTranslate('My Greeting Cards');
  const coinsLabel = useGoogleTranslate('Coins');
  const processingLabel = useGoogleTranslate('Processing...');
  const buyBtn = useGoogleTranslate('Buy');
  const searchPlaceholder = useGoogleTranslate('Search...');
  const loadingLabel = useGoogleTranslate('Loading...');
  const noGiftsLabel = useGoogleTranslate('No gifts available');
  const purchaseSuccessPrefix = useGoogleTranslate('Successfully purchased');
  const purchaseFailedLabel = useGoogleTranslate('Purchase failed');
  const giftDescription = useGoogleTranslate(
    'Make their day by sending a thoughtful gift or greeting card because great connections start with thoughtful gestures.'
  );

  const [giftType, setGiftType] = useState('stickers');
  const heroTitle = giftType === 'cards' ? greetingCardsPageTitle : pageTitle;

  usePageTitle(heroTitle);
  const [activeTab, setActiveTab] = useState('buy');
  const [quantities, setQuantities] = useState({});
  const [purchasingId, setPurchasingId] = useState(null);
  const [translatedTexts, setTranslatedTexts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const langCode = useUserStore((s) => s.appLanguage ?? 'en');

  // Queries for stickers
  const { data: virtualGifts, isLoading: isLoadingVirtual } = useQuery({
    queryKey: ['virtual-gifts', currentPage],
    queryFn: () => getVirtualGifts(currentPage),
    enabled: giftType === 'stickers' && activeTab === 'buy',
  });

  const { data: myVirtualGifts, isLoading: isLoadingMyVirtual } = useQuery({
    queryKey: ['my-virtual-gifts', currentPage],
    queryFn: () => getMyVirtualGifts(currentPage),
    enabled: giftType === 'stickers' && activeTab === 'my',
  });

  // Queries for greeting cards
  const { data: greetingCards, isLoading: isLoadingCards } = useQuery({
    queryKey: ['greeting-cards', currentPage],
    queryFn: () => getGreetingCards(currentPage),
    enabled: giftType === 'cards' && activeTab === 'buy',
  });

  const { data: myGreetingCards, isLoading: isLoadingMyCards } = useQuery({
    queryKey: ['my-greeting-cards', currentPage],
    queryFn: () => getMyGreetingCards(currentPage),
    enabled: giftType === 'cards' && activeTab === 'my',
  });

  // Translate dynamic gift titles and descriptions when data or language changes
  React.useEffect(() => {
    let cancelled = false;

    async function run() {
      if (langCode === 'en') return;

      const lists = [
        virtualGifts?.data,
        myVirtualGifts?.data,
        greetingCards?.data,
        myGreetingCards?.data,
      ].filter(Boolean);

      if (!lists.length) return;

      const texts = new Set();
      lists.forEach((list) => {
        list.forEach((item) => {
          const gift = item.gift || item;
          if (gift?.title) texts.add(String(gift.title));
          if (gift?.description) texts.add(String(gift.description));
        });
      });

      const newEntries = {};
      await Promise.all(
        Array.from(texts).map(async (txt) => {
          if (!txt || translatedTexts[txt]) return;
          const translated = await translateText(txt, langCode);
          newEntries[txt] = translated;
        })
      );

      if (!cancelled && Object.keys(newEntries).length) {
        setTranslatedTexts((prev) => ({ ...prev, ...newEntries }));
      }
    }

    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [virtualGifts, myVirtualGifts, greetingCards, myGreetingCards, langCode]);

  const getQuantity = (giftId) => quantities[giftId] || 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleGiftTypeChange = (type) => {
    setGiftType(type);
    setActiveTab('buy');
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleIncrement = (giftId) => {
    setQuantities(prev => ({
      ...prev,
      [giftId]: (prev[giftId] || 1) + 1
    }));
  };

  const handleDecrement = (giftId) => {
    setQuantities(prev => ({
      ...prev,
      [giftId]: Math.max(1, (prev[giftId] || 1) - 1)
    }));
  };

  const handleBuyGift = async (gift) => {
    try {
      setPurchasingId(gift.id);
      const quantity = getQuantity(gift.id);

      const response = await purchaseGift(gift.id, quantity);

      if (response?.status) {
        showToast(`${purchaseSuccessPrefix} ${gift.title}!`, 'success');
        // Reset quantity
        setQuantities(prev => ({ ...prev, [gift.id]: 1 }));
      } else {
        showToast(response?.message || purchaseFailedLabel, 'error');
      }
    } catch (error) {
      console.error('Error purchasing gift:', error);
      showToast(error?.message || purchaseFailedLabel, 'error');
    } finally {
      setPurchasingId(null);
    }
  };

  const renderGiftCard = (item, showBuyButton = true) => {
    // Handle both response structures: direct gift object or nested gift.gift
    const gift = item.gift || item;
    const giftId = showBuyButton ? gift.id : item.id;
    const ownedQuantity = !showBuyButton ? (item.quantity || 1) : null;

    const quantity = getQuantity(gift.id);
    const totalCoins = gift.coins * quantity;
    const isPurchasing = purchasingId === gift.id;
    const translatedTitle = translatedTexts[gift.title] || gift.title;
    const translatedDescription =
      translatedTexts[gift.description] || gift.description;

    return (
      <Col key={giftId} xs={12} sm={6} md={4} className="mb-4 gift-shop-col">
        <div className="gift-card">
          <div className="gift-image-wrapper">
            <img
              src={gift.image?.path}
              alt={gift.title}
              className="gift-image"
            />
          </div>

          <div className="gift-content">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="gift-title mb-0 pb-0">{translatedTitle}</h4>
              {!showBuyButton && ownedQuantity && (
                <div className="gift-owned-badge">{ownedQuantity}</div>
              )}
            </div>

            {showBuyButton && (
              <>
                <div className="gift-coins-row">
                  <div className="gift-coins-display">
                    <span className="gift-coins-label">{totalCoins} {coinsLabel}</span>
                    <img src={smallCoinIcon} alt="Coin" className="coin-icon-small" />
                  </div>

                  <div className="gift-quantity-controls">
                    <button
                      className="quantity-btn"
                      onClick={() => handleDecrement(gift.id)}
                      disabled={isPurchasing}
                    >
                      -
                    </button>
                    <span className="quantity-display">{quantity}</span>
                    <button
                      className="quantity-btn"
                      onClick={() => handleIncrement(gift.id)}
                      disabled={isPurchasing}
                    >
                      +
                    </button>
                  </div>
                </div>

                <p className="gift-description">{translatedDescription}</p>

                <CustomButton
                  onClick={() => handleBuyGift(gift)}
                  disabled={isPurchasing}
                  loading={isPurchasing}
                  className="gift-buy-btn"
                >
                  <FaGift className="me-2" />
                  {isPurchasing ? processingLabel : buyBtn}
                </CustomButton>
              </>
            )}

            {!showBuyButton && (
              <>
                <div className="gift-coins-display mb-2">
                  <span className="gift-coins-label">{gift.coins} {coinsLabel}</span>
                  <img src={smallCoinIcon} alt="Coin" className="coin-icon-small" />
                </div>
                <p className="gift-description">{translatedDescription}</p>
              </>
            )}
          </div>
        </div>
      </Col>
    );
  };

  const renderGiftList = (data, isLoading, showBuyButton = true) => {
    if (isLoading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{loadingLabel}</span>
          </Spinner>
          <p className="mt-3">{loadingLabel}</p>
        </div>
      );
    }

    if (!data?.data || data.data.length === 0) {
      return (
        <div className="text-center py-5">
          <p className="mt-3">{noGiftsLabel}</p>
        </div>
      );
    }

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filteredItems = normalizedSearch
      ? data.data.filter((item) => {
        const gift = item.gift || item;
        const translatedTitle = translatedTexts[gift.title] || gift.title || '';
        const translatedDescription = translatedTexts[gift.description] || gift.description || '';
        const haystack = `${translatedTitle} ${translatedDescription}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      })
      : data.data;

    if (filteredItems.length === 0) {
      return (
        <div className="text-center py-5">
          <p className="mt-3">{noGiftsLabel}</p>
        </div>
      );
    }

    const totalPages = data?.meta?.last_page || 1;
    const currentPage = data?.meta?.current_page || 1;
    const showPagination = !normalizedSearch && totalPages > 1;

    return (
      <>
        <Row className="justify-content-center">
          {filteredItems.map((gift) => renderGiftCard(gift, showBuyButton))}
        </Row>
        {showPagination && (
          <div className="gift-pagination">
            <Pagination>
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === currentPage}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
        {showPagination && (
          <div className="pagination-info">
            Page <span className="current-page">{currentPage}</span> of <span className="total-pages">{totalPages}</span>
          </div>
        )}
      </>
    );
  };

  return (
    <section className="gift-figma-page">
      <span className="gift-figma-decor gift-figma-decor-ring-left-top" aria-hidden="true" />
      <span className="gift-figma-decor gift-figma-decor-ring-left-bottom" aria-hidden="true" />
      <span className="gift-figma-decor gift-figma-decor-ring-right-mid" aria-hidden="true" />
      <span className="gift-figma-decor gift-figma-decor-ring-right-top" aria-hidden="true" />
      <img src={heartDecor} alt="" className="gift-figma-decor gift-figma-decor-heart" aria-hidden="true" />

      <div className="gift-figma-hero">
        <Container>
          <h1>{heroTitle}</h1>
          <p className="gift-store-description">{giftDescription}</p>
        </Container>
      </div>

      <div className="gift-figma-main">
        <Container className="gift-figma-container">
          <Tab.Container activeKey={activeTab} onSelect={(k) => handleTabChange(k)}>
            <Nav variant="pills" className="gift-tabs mb-4">
              <Nav.Item>
                <Nav.Link eventKey="buy">
                  {giftType === 'stickers' ? buyGiftsLabel : buyGreetingCardsLabel}
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="my">
                  {giftType === 'stickers' ? myGiftsLabel : myGreetingCardsLabel}
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <div className="gift-figma-toolbar">
              <div className="gift-type-select-wrap">
                <Form.Select
                  value={giftType}
                  onChange={(e) => handleGiftTypeChange(e.target.value)}
                  className="gift-type-select"
                >
                  <option value="stickers">{giftsLabel}</option>
                  <option value="cards">{greetingCardsLabel}</option>
                </Form.Select>
              </div>

              <div className="gift-search-wrap">
                <div className="gift-search-pill">
                  <input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <FiSearch />
                </div>
              </div>
            </div>

            <Tab.Content>
              <Tab.Pane eventKey="buy">
                {giftType === 'stickers'
                  ? renderGiftList(virtualGifts, isLoadingVirtual, true)
                  : renderGiftList(greetingCards, isLoadingCards, true)
                }
              </Tab.Pane>

              <Tab.Pane eventKey="my">
                {giftType === 'stickers'
                  ? renderGiftList(myVirtualGifts, isLoadingMyVirtual, false)
                  : renderGiftList(myGreetingCards, isLoadingMyCards, false)
                }
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Container>
      </div>
    </section>
  );
};

export default GiftStore;
